'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import { RiCheckLine, RiCloseLine, RiErrorWarningLine, RiInformationLine } from '@remixicon/react';
import type { RemixiconComponentType } from '@remixicon/react';
import { useTranslations } from 'next-intl';

import { spring, toastShell, toastSlide } from '@/lib/motion';

/**
 * ÜRÜNÜN TEK "OLDU / OLMADI" BİLDİRİMİ — sağ alttan giren kart.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 Bir işlemin SONUCU bildirilecekse yüzey BUDUR
 * ═══════════════════════════════════════════════════════════════════════════
 * "Ayarlar kaydedildi", "Kural silinemedi", "Davet gönderildi" — kullanıcının
 * az önce yaptığı bir şeye verilen cevap. Kendi yeşil satırını, kendi kırmızı
 * kutunu yazma.
 *
 * Üç yüzey birbirine karıştırılmasın; üçü farklı sorulara cevap veriyor:
 *
 *   • `Toast` (burası) bir SONUÇ. Kullanıcı bir şey yaptı, cevabı geldi,
 *     birkaç saniye sonra kendiliğinden gidiyor. Sayfanın düzenini bozmuyor.
 *   • `DashNote` bir DURUM: "henüz reklam hesabı bağlı değil". Kullanıcı bir
 *     şey yapmadan da orada duruyor ve düzelene kadar kalıyor.
 *   • Alan altındaki doğrulama metni bir KURAL: "en az 2 karakter". Alanın
 *     yanında kalır, uçmaz.
 *
 * ---------------------------------------------------------------------------
 * Neden havuzda (`components/ui/`) ve CSS'i `globals.css`te
 * ---------------------------------------------------------------------------
 * `ProgressRail`, `GaugeArc` ve tarih seçicileriyle aynı gerekçe: kuralları
 * `dashboard.css`e yazılsaydı yalnız müşteri panelinde çalışırdı. Oysa aynı
 * bildirim /admin'de (kendi `admin.css`i var), giriş ekranlarında ve landing
 * formlarında da lazım. Sağlayıcı `[locale]/layout.tsx`te, yani uygulamanın
 * tamamında tek bir deste var.
 *
 * ---------------------------------------------------------------------------
 * Neden sağ ALT
 * ---------------------------------------------------------------------------
 * Üst sağ, panelin üst çubuğundaki workspace seçicisi ve hesap menüsüyle aynı
 * köşe; bildirim tam da o menülerin üstüne oturuyordu. Alt kenarda çakışacak
 * bir şey yok (tek `position: fixed` komşu sol çekmece) ve dokunmatikte kapat
 * düğmesi başparmağın erişebildiği yere düşüyor.
 *
 * ---------------------------------------------------------------------------
 * Köşe işaretleri (`DashNote`'un ölçü kareleri) BİLEREK yok
 * ---------------------------------------------------------------------------
 * O kareler kutunun kenar çizgisini KESEN boşluklar ve dolgularının arkadaki
 * yüzeyin rengi olması gerekiyor. Uçan bir katman rastgele bir içeriğin
 * üstünde duruyor — arkasında ne olduğu bilinemez. Bu yüzden bildirim panelin
 * öteki uçan katmanlarının (`.pick-panel`, `.dash-drop-panel`) derisini
 * giyiyor: beyaz, 12px yarıçap, aynı gölge. Haberi ton taşıyor, köşe değil.
 */

/**
 * Üç ton, dördüncüsü yok.
 *
 * - `ok`    — iş oldu.
 * - `error` — iş olmadı. Kullanıcının yeniden denemesi ya da bir şeyi
 *             düzeltmesi gerekiyor.
 * - `info`  — oldu ama kayıtsız şartsız değil: "kaydedildi, 2 tercih yönetici
 *             kilidinde", "kural çalıştı ama koşul tutmadı".
 *
 * `warn` YOK ve bu bilinçli: engellemeyen bir uyarı bir SONUÇ değil bir
 * DURUM'dur, yeri `DashNote`. Uçup giden bir kutuya yazılırsa kullanıcı onu
 * okumadan kaybediyor.
 */
export type ToastTone = 'ok' | 'error' | 'info';

export type ToastOptions = {
  tone?: ToastTone;
  /** Kalın ilk satır. Tek cümlelik sonuçlarda gereksiz — atlanabilir. */
  title?: string;
  /**
   * Kendiliğinden kapanma süresi (ms). `null` verilirse kart kullanıcı
   * kapatana kadar duruyor — geri alınamaz bir işlemin sonucu gibi.
   * Verilmezse tona göre varsayılan uygulanıyor (aşağıya bakınız).
   */
  duration?: number | null;
  /** Sonucun bir devamı varsa: "Geri al", "Faturayı gör". */
  action?: { label: string; onClick: () => void };
  /**
   * KARTIN SAĞ UCUNDAKİ KÜÇÜK KARE — sonucun kendisinin bir yüzü varsa.
   *
   * ═════════════════════════════════════════════════════════════════════════
   * 🔴 Bir ÖNİZLEME değil bir İŞARET (21 Ağu 2026, kullanıcı)
   * ═════════════════════════════════════════════════════════════════════════
   * Kutu 44px'lik bir kare ve BÜYÜMÜYOR: bildirim kartı bir görüntüleyici
   * değil. Üretilen kreatife bakmanın yeri geçmiş ekranı — kare oraya giden
   * kapı, orada görülecek şeyin kendisi değil. Kartın içinde açılan bir
   * önizleme, uçup giden bir katmanı ikinci bir ekrana çevirirdi.
   *
   * Slot BOŞ bırakılmış: bileşen ne bastığını bilmiyor, yalnız yerini
   * ayırıyor. Kreatif tezgâhı buraya `CreativeThumb` koyuyor (video kapak
   * karesi ve yedek ikon mantığı orada, bir kez yazılı); başka bir ekran
   * bambaşka bir şey koyabilir.
   *
   * ⚠️ Kareye tıklamak kartı KAPATIYOR — `action` ile aynı sözleşme. İçine
   * konan bağlantı kullanıcıyı başka bir ekrana götürüyor; arkada kalan
   * kendiliğinden kapanmayan (`duration: null`) bir kart, gidilen sayfada
   * asılı kalırdı.
   */
  media?: ReactNode;
  /** Kart hangi sebeple giderse gitsin (süre doldu, kapatıldı) bir kez çağrılır. */
  onClose?: () => void;
};

export type ToastInput = ToastOptions & { text: ReactNode };

type ToastRecord = ToastInput & { id: string; tone: ToastTone; duration: number | null };

export type ToastApi = {
  show: (input: ToastInput) => string;
  ok: (text: ReactNode, options?: ToastOptions) => string;
  error: (text: ReactNode, options?: ToastOptions) => string;
  info: (text: ReactNode, options?: ToastOptions) => string;
  dismiss: (id: string) => void;
  clear: () => void;
};

/**
 * Varsayılan süreler tona göre AYRI.
 *
 * Hata daha uzun duruyor çünkü okunduktan sonra bir karar gerektiriyor
 * ("yeniden dene?" / "neyi düzelteceğim?"); başarı yalnızca teyit, göz ucuyla
 * okunup geçiliyor. Tek bir süre ikisine de yanlış: başarı için uzun, hata
 * için kısa olurdu.
 */
const DURATION: Record<ToastTone, number> = {
  ok: 5000,
  info: 6000,
  error: 9000,
};

/**
 * Aynı anda en fazla üç kart.
 *
 * Toplu bir işlem (yedi kuralı birden duraklatmak) yedi ayrı sonuç üretebilir
 * ve ekranın yarısı bildirime dönerdi. Üçün üstündeki EN ESKİ kart normal
 * çıkış animasyonuyla gidiyor — sessizce silinmiyor, `onClose`'u da çağrılıyor.
 */
const MAX_VISIBLE = 3;

const ToastContext = createContext<ToastApi | null>(null);

/**
 * Sağlayıcı yokken çöken bir bildirim çağrısı, bildirmek istediği hatadan
 * daha büyük bir hata olurdu. `useDashChrome` ile aynı tercih: fırlatma, boş
 * dön — ama geliştirmede sessiz kalma.
 */
const NO_PROVIDER: ToastApi = {
  show: () => warnMissing(),
  ok: () => warnMissing(),
  error: () => warnMissing(),
  info: () => warnMissing(),
  dismiss: () => undefined,
  clear: () => undefined,
};

function warnMissing(): string {
  if (process.env.NODE_ENV !== 'production') {
    console.warn('[toast] ToastProvider ağaçta yok — bildirim gösterilmedi.');
  }
  return '';
}

export function useToast(): ToastApi {
  return useContext(ToastContext) ?? NO_PROVIDER;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastRecord[]>([]);

  /* Kimlik sayaçtan, `Math.random()` ya da `Date.now()` değil: ikisi de
     sunucu ve tarayıcıda farklı değer üretiyor. Bildirimler yalnız etkileşimle
     doğsa da kimliğin kaynağı hep aynı olsun. */
  const seq = useRef(0);

  /* `onClose` durumda DEĞİL ref'te: geri çağırma bir state güncelleyicisinin
     içinden çağrılamaz (React güncelleyicileri StrictMode'da iki kez
     çalıştırıyor, geri çağırma da iki kez tetiklenirdi). */
  const closers = useRef(new Map<string, () => void>());

  const dismiss = useCallback((id: string) => {
    const done = closers.current.get(id);
    closers.current.delete(id);
    setItems((list) => list.filter((item) => item.id !== id));
    done?.();
  }, []);

  const show = useCallback((input: ToastInput) => {
    const tone = input.tone ?? 'info';
    seq.current += 1;
    const id = `toast-${seq.current}`;

    if (input.onClose) closers.current.set(id, input.onClose);

    setItems((list) => [
      ...list,
      {
        ...input,
        id,
        tone,
        /* `undefined` "karar sende", `null` "hiç kapanma" demek — ikisi ayrı
           sorular, `??` ile birleştirilemez. */
        duration: input.duration === undefined ? DURATION[tone] : input.duration,
      },
    ]);

    return id;
  }, []);

  /* Geri çağırmalar güncelleyicinin DIŞINDA — `dismiss` ile aynı gerekçe.
     `setItems(list => …)` içinde çağrılsalardı StrictMode'un iki kez
     çalıştırdığı güncelleyici her `onClose`'u da iki kez tetiklerdi. */
  const clear = useCallback(() => {
    const pending = [...closers.current.values()];
    closers.current.clear();
    setItems([]);
    for (const done of pending) done();
  }, []);

  /* Taşan kartlar burada, render'dan SONRA düşürülüyor — `show` içinde
     `slice` ile kesilseydi düşen kartın `onClose`'u hiç çağrılmaz ve onu
     bekleyen ekranın durumu (`flash`) sonsuza kadar dolu kalırdı. */
  useEffect(() => {
    if (items.length <= MAX_VISIBLE) return;
    for (const item of items.slice(0, items.length - MAX_VISIBLE)) dismiss(item.id);
  }, [items, dismiss]);

  const api = useMemo<ToastApi>(
    () => ({
      show,
      ok: (text, options) => show({ ...options, tone: 'ok', text }),
      error: (text, options) => show({ ...options, tone: 'error', text }),
      info: (text, options) => show({ ...options, tone: 'info', text }),
      dismiss,
      clear,
    }),
    [show, dismiss, clear],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport items={items} onDismiss={dismiss} onClear={clear} />
    </ToastContext.Provider>
  );
}

/* ========================================================================== */
/* Deste                                                                       */
/* ========================================================================== */

function ToastViewport({
  items,
  onDismiss,
  onClear,
}: {
  items: ToastRecord[];
  onDismiss: (id: string) => void;
  onClear: () => void;
}) {
  const t = useTranslations('toast');

  /* Portal yalnız tarayıcıda: `document` sunucuda yok.
     `role="status"` taşıyan kap sonradan eklenirse bazı ekran okuyucular
     içeriğini duyurmuyor — ama kap ilk hidrasyonda, ilk bildirimden çok önce
     kuruluyor (bildirim ancak bir kullanıcı eylemiyle doğuyor), yani canlı
     bölge her zaman içerikten önce yerinde. */
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  /* İmleç ya da klavye odağı destenin üzerindeyken sayaç DURUYOR: kullanıcı
     okumak için üstüne geldiyse cümle elinin altından kaçmamalı. */
  const [paused, setPaused] = useState(false);

  if (!mounted) return null;

  return createPortal(
    <div
      className="toast-viewport"
      role="status"
      aria-live="polite"
      aria-label={t('region')}
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setPaused(false);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') onClear();
      }}
    >
      {/* `initial={false}`: sayfa açılışında deste zaten boş; ilk kart
          eklendiğinde giriş animasyonu çalışsın diye kapatılan şey yalnızca
          "mount anında hepsini oynat" davranışı. */}
      <AnimatePresence initial={false}>
        {items.map((item) => (
          <ToastCard
            key={item.id}
            item={item}
            paused={paused}
            closeLabel={t('dismiss')}
            onDismiss={onDismiss}
          />
        ))}
      </AnimatePresence>
    </div>,
    document.body,
  );
}

const ICON: Record<ToastTone, RemixiconComponentType> = {
  ok: RiCheckLine,
  error: RiErrorWarningLine,
  info: RiInformationLine,
};

function ToastCard({
  item,
  paused,
  closeLabel,
  onDismiss,
}: {
  item: ToastRecord;
  paused: boolean;
  closeLabel: string;
  onDismiss: (id: string) => void;
}) {
  const Icon = ICON[item.tone];

  /* Kalan süre ref'te tutuluyor: durdur/devam et her seferinde sayacı baştan
     başlatsaydı imleci kartın üstünde birkaç kez gezdiren kullanıcı bildirimi
     hiç kapatamazdı. */
  const remaining = useRef(item.duration ?? 0);

  useEffect(() => {
    if (item.duration === null || paused) return;

    const startedAt = performance.now();
    const timer = window.setTimeout(() => onDismiss(item.id), remaining.current);

    return () => {
      window.clearTimeout(timer);
      remaining.current = Math.max(0, remaining.current - (performance.now() - startedAt));
    };
  }, [item.duration, item.id, paused, onDismiss]);

  return (
    <motion.div
      className="toast-item"
      /* Deste yalnız `layout` ile kayıyor: yeni kart eklenince kap büyüyor ve
         kabuk alt kenara sabit olduğu için eskiler yukarı çıkıyor. Ölçüyü
         Motion transform'a çeviriyor — `top`/`height` animasyonlanmıyor. */
      layout
      variants={toastShell}
      initial="hidden"
      animate="show"
      exit="exit"
    >
      <motion.div className="toast" data-tone={item.tone} variants={toastSlide}>
        {/* İkonun kendi girişi var ve varyantlardan KOPUK (`animate` prop'u
            yazılı): `ok` tonunda kart yerine otururken işaret bir kez zıplıyor
            — DESIGN.md §4 "başarı anlarında cesur olunur". Hata ve bilgi
            tonunda kutlanacak bir şey yok, orada aynı jest yersiz olurdu. */}
        <motion.span
          className="toast-icon"
          initial={{ scale: 0.4 }}
          animate={{ scale: 1 }}
          transition={item.tone === 'ok' ? { ...spring.bouncy, delay: 0.06 } : spring.snap}
        >
          <Icon size={18} aria-hidden="true" />
        </motion.span>

        <div className="toast-body" data-lead={item.title ? 'title' : 'text'}>
          {item.title ? <strong className="toast-title">{item.title}</strong> : null}
          <span className="toast-text">{item.text}</span>

          {item.action ? (
            <button
              type="button"
              className="toast-action"
              onClick={() => {
                item.action?.onClick();
                onDismiss(item.id);
              }}
            >
              {item.action.label}
            </button>
          ) : null}
        </div>

        {/* Kare GÖVDEDEN SONRA, kapatma düğmesinden önce: kart soldan sağa
            "ne oldu → neyle sonuçlandı → kapat" diye okunuyor. Başa konsaydı
            tonu taşıyan işaretle yer değiştirir ve iki farklı kartta iki
            farklı şey aynı köşeden başlardı. */}
        {item.media ? (
          <div
            className="toast-media"
            /* Tıklama kartı kapatıyor — gerekçe `ToastOptions.media`da.
               Klavye de aynı yolu izliyor: içerideki bağlantıda Enter bir
               `click` doğuruyor ve buraya kabarıyor. */
            onClick={() => onDismiss(item.id)}
          >
            {item.media}
          </div>
        ) : null}

        <button type="button" className="toast-close" onClick={() => onDismiss(item.id)}>
          <RiCloseLine size={16} aria-hidden="true" />
          <span className="sr-only">{closeLabel}</span>
        </button>
      </motion.div>
    </motion.div>
  );
}
